import React, { useState } from 'react';
import { MentionTextarea } from './mentions';
import type { RiskLevel } from './types';

/**
 * The one comment composer (spec §8, 2026-08-20).
 *
 * Owns the finding-vs-consultation chooser and the fields each kind needs.
 * DocumentViewer and SnapshotViewer previously carried near-verbatim copies
 * of this form, and they had already drifted — the same class of duplication
 * that put three rounds of marker fixes on the render path prod doesn't use.
 * The chooser must exist exactly once, so the form it gates does too.
 *
 * The chooser comes FIRST, so each form shows only its own fields:
 *   - Suggest a fix   → severity + issue + suggested fix (a finding: records
 *                       risk, will carry a disposition, closed by the DM)
 *   - Ask for input   → issue only (a consultation: seeks support, no
 *                       severity, closed by being answered). This is
 *                       deliberate — "the user should not have unnecessary
 *                       fields to fill in when just seeking guidance."
 *
 * The host owns anchor state (selection / coordinates / timestamp) and the
 * POST; this component hands back what the author wrote plus the kind. State
 * resets by unmounting — both hosts unmount the form when the anchor clears.
 */

export interface ComposerSubmission {
  kind: 'finding' | 'consultation';
  issue: string;
  severity?: RiskLevel;
  suggestedFix?: string;
  // Spec §3.1 / Phase 6 — literal replacement for the quoted selection.
  // Only offered when there IS a selection; the system applies exactly
  // this, in place of exactly that, or not at all.
  proposedText?: string;
}

interface CommentComposerProps {
  // Anchor label, e.g. "Comment on selected area" / "Comment at 0:12".
  title: string;
  // Selected text or section context to quote above the form.
  quote?: string | null;
  quoteBorderColor?: string;
  // Layout view: crop of the marked region, shown where the quote would be.
  thumbnail?: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  // True when `quote` is an exact text selection in an editable format
  // (docx/pptx, or a pdf whose companion is) — enables "Replacement text".
  canPropose?: boolean;
  // False for mentioned participants (D69): the chooser is skipped and the
  // composer opens straight in consultation mode — they may ask, not find.
  allowFinding?: boolean;
  submitting: boolean;
  onSubmit: (body: ComposerSubmission) => void | Promise<void>;
  onCancel: () => void;
}

const CommentComposer: React.FC<CommentComposerProps> = ({
  title, quote, quoteBorderColor, thumbnail, getAuthHeaders, canPropose = false, allowFinding = true, submitting, onSubmit, onCancel
}) => {
  const [mode, setMode] = useState<'finding' | 'consultation' | null>(allowFinding ? null : 'consultation');
  const [issue, setIssue] = useState('');
  const [severity, setSeverity] = useState<RiskLevel>('minor');
  const [suggestedFix, setSuggestedFix] = useState('');
  const [proposedText, setProposedText] = useState('');
  const proposing = canPropose && !!quote;

  // 2026-08-26 (Fredrik, docx walk) — a consultation with no @mention reaches
  // NOBODY, silently. The mention is the addressing mechanism: the dashboard
  // feed is built from "comments mentioning you" + "threads you authored"
  // (routes/contentReview.js), and the notification is gated on
  // mentionedTeamMembers.length > 0. Fredrik filed two consultations; the one
  // without a handle notified no one and appeared on no dashboard, while
  // returning a perfectly normal success.
  //
  // Deliberately a WARNING, not a block: a mention-less consultation is still
  // visible to anyone who opens the cycle (GET /cycles/:id/consultations lists
  // every root regardless of mentions), so it has a legitimate use as a note
  // on the record. Blocking would remove that.
  //
  // The regex is HANDLE_RE from services/contentReview/mentionResolverService.js,
  // copied deliberately rather than approximated — a warning that disagrees
  // with the resolver is worse than no warning.
  //
  // Limit worth knowing: this detects a handle, it does not RESOLVE one.
  // "@nobody" parses and silences this warning while still resolving to
  // teamMemberId: null on the server. Predicting resolution client-side would
  // need a lookup the composer does not have, and guessing it would reintroduce
  // exactly the disagreement this comment warns against.
  const mentionsSomeone = /(^|[\s(])@([A-Za-z0-9._+-]+)/.test(issue);
  const silentConsultation = mode === 'consultation' && !!issue.trim() && !mentionsSomeone;

  const submit = () => {
    if (!issue.trim() || !mode) return;
    onSubmit(mode === 'finding'
      ? { kind: 'finding', issue: issue.trim(), severity, suggestedFix: suggestedFix.trim(), ...(proposing && proposedText.trim() ? { proposedText: proposedText } : {}) }
      : { kind: 'consultation', issue: issue.trim() });
  };

  return (
    <div className="viewer-comment-form" data-testid="comment-composer">
      <h4 style={{ margin: '0 0 8px', color: '#123250' }}>{title}</h4>

      {thumbnail && (
        <img
          src={thumbnail}
          alt="Marked region"
          style={{ maxWidth: '100%', maxHeight: 140, display: 'block', border: '1px solid #ddd', borderRadius: 4, margin: '0 0 8px' }}
        />
      )}
      {quote && (
        <div
          className="viewer-selection-quote"
          style={{
            ...(quoteBorderColor ? { borderLeftColor: quoteBorderColor } : {}),
            maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap'
          }}
        >
          {quote}
        </div>
      )}

      {mode === null ? (
        <div className="composer-chooser">
          <p style={{ fontSize: 13, color: '#555', margin: '0 0 10px' }}>
            What do you want to do here?
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="review-btn review-btn-primary"
              data-testid="choose-finding"
              onClick={() => setMode('finding')}
              style={{ fontSize: 13 }}
            >
              Suggest a fix
            </button>
            <button
              className="review-btn review-btn-outline"
              data-testid="choose-consultation"
              onClick={() => setMode('consultation')}
              style={{ fontSize: 13, borderColor: '#7c3aed', color: '#7c3aed' }}
            >
              Ask for input
            </button>
            <button
              className="review-btn review-btn-outline"
              onClick={onCancel}
              style={{ fontSize: 13, marginLeft: 'auto' }}
            >
              Cancel
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#8a94a6', margin: '8px 0 0', lineHeight: 1.4 }}>
            A <strong>fix</strong> records a risk level and goes to the decision maker.{' '}
            <strong>Asking for input</strong> starts a consultation thread — @mention who you
            need; it closes when answered.
          </p>
        </div>
      ) : (
        <>
          <div className="review-form-group">
            <label>{mode === 'finding' ? 'Issue *' : 'Your question *'}</label>
            {/* 2026-08-21 (Fredrik) — @mentions are the consultation path and
                nothing else. On a finding the field is a plain textarea: a
                handle typed here stays plain text, notifies nobody and grants
                no access, because the Consultations panel does not track
                findings and an ask filed there was invisible. */}
            {/* 2026-08-27 (Legal reviewer, live video cycle) — autoFocus on
                BOTH branches. The chooser is what mounts this field, so the
                click that picks a kind is also the click that should put the
                caret in it; he was having to click the box by hand every
                time. Mount-time autoFocus rather than an effect on `mode`
                because each branch is a distinct element that mounts exactly
                when its mode is chosen — and it covers the mentioned-
                participant case too, where allowFinding=false opens straight
                in consultation mode with no chooser click at all. */}
            {mode === 'consultation' ? (
              <MentionTextarea
                value={issue}
                onChange={setIssue}
                getAuthHeaders={getAuthHeaders}
                placeholder="What do you need input on? @mention who you are asking"
                rows={3}
                autoFocus
              />
            ) : (
              <textarea
                value={issue}
                onChange={e => setIssue(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                data-testid="finding-issue"
                autoFocus
              />
            )}
            {mode === 'finding' && (
              <p style={{ fontSize: 11, color: '#8a94a6', margin: '4px 0 0', lineHeight: 1.4 }}>
                Need a colleague's input? Cancel and choose <strong>Ask for input</strong> —
                findings go to the decision maker, questions go to people.
              </p>
            )}
          </div>

          {mode === 'finding' && (
            <>
              <div className="review-form-group">
                <label htmlFor="composer-risk-level">Risk level</label>
                <select
                  id="composer-risk-level"
                  value={severity}
                  onChange={e => setSeverity(e.target.value as RiskLevel)}
                >
                  <option value="critical">Critical</option>
                  <option value="major">Major</option>
                  <option value="minor">Minor</option>
                  <option value="suggestion">Suggestion</option>
                  <option value="none">No risk</option>
                </select>
              </div>
              {/* 2026-08-26 — this field was inline-styled at 13px / 6px
                  padding / #ced4da / radius 6, i.e. visibly unlike every other
                  field in the same panel, and it is the ONLY one a reviewer
                  uses to change the document. It now takes review-form-group
                  like its siblings, so the shared rule owns focus, hover and
                  disabled. It is also now a SIBLING of Suggested Fix rather
                  than nested inside its group — nesting the class in itself
                  doubled the bottom margin and made the two read as one field. */}
              {proposing && (
                <div className="review-form-group" data-testid="proposed-text-block">
                    <label>Replacement text <span style={{ fontWeight: 400, color: '#6c757d' }}>(optional — applied exactly in place of the selected text)</span></label>
                    <textarea
                      data-testid="proposed-text"
                      value={proposedText}
                      onChange={e => setProposedText(e.target.value)}
                      rows={2}
                      placeholder="Type the text that should replace the selection — nothing else"
                    />
                    {proposedText.trim() && (
                      <div
                        data-testid="proposed-preview"
                        style={{
                          fontSize: 12, marginTop: 8, padding: '8px 10px', lineHeight: 1.5,
                          background: '#fff', border: '1px solid #e9ecef',
                          borderLeft: '3px solid #0f5132', borderRadius: '0 6px 6px 0'
                        }}
                      >
                        <div style={{ color: '#842029' }}><span style={{ fontWeight: 600 }}>Before:</span> <s>{quote}</s></div>
                        <div style={{ color: '#0f5132', marginTop: 2 }}><span style={{ fontWeight: 600 }}>After:</span> {proposedText}</div>
                        <div style={{ color: '#6c757d', marginTop: 6, paddingTop: 6, borderTop: '1px solid #f1f3f5' }}>
                          Applied only if this exact selection is still in the working file; otherwise it becomes an in-file comment for the producer.
                        </div>
                      </div>
                    )}
                </div>
              )}
              <div className="review-form-group">
                <label>Suggested Fix</label>
                {/* A plain textarea on purpose: the backend resolves mentions
                    from the issue body only, so autocomplete here would
                    promise a notification that never fires. */}
                <textarea
                  value={suggestedFix}
                  onChange={e => setSuggestedFix(e.target.value)}
                  placeholder="Optional: suggest how to fix..."
                  rows={2}
                />
              </div>
            </>
          )}

          {silentConsultation && (
            <div
              data-testid="consultation-no-mention-warning"
              style={{
                fontSize: 12, lineHeight: 1.45, margin: '0 0 10px', padding: '8px 10px',
                background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 6, color: '#7a5a00'
              }}
            >
              <strong>This won't notify anyone.</strong> Add an <strong>@mention</strong> to send it
              to someone — without one it is only visible to whoever opens this cycle.
            </div>
          )}

          <div className="review-action-bar">
            <button
              className="review-btn review-btn-primary"
              onClick={submit}
              disabled={submitting || !issue.trim()}
            >
              {submitting
                ? 'Submitting…'
                : mode === 'finding' ? 'Add Comment' : 'Ask'}
            </button>
            <button
              className="review-btn review-btn-outline"
              onClick={() => setMode(null)}
              disabled={submitting}
            >
              Back
            </button>
            <button className="review-btn review-btn-outline" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CommentComposer;
